import React from 'react';
import type { TFunction } from 'i18next';
import { Trans } from 'react-i18next';

/** `<hidden>…</hidden>` in a string renders as visually hidden text (global `.sr-only`). */
const HIDDEN_TEXT = { hidden: <span className="sr-only" /> };

interface HiddenContextTextProps {
  /** The `t` of the component's namespace. */
  t: TFunction;
  /** A string whose `<hidden>…</hidden>` part is spoken but not shown. */
  i18nKey: string;
  values?: Record<string, unknown>;
}

/**
 * A control's label with context that only assistive technology gets (OA-24).
 *
 * "Delete" alone does not say which file. With `"Delete <hidden>{{filename}}</hidden>"` the button
 * shows "Delete" and is named "Delete report.pdf". The control then has one labelling strategy --
 * its content -- instead of an `aria-label` overriding the visible text (STRUCTURE-23), and the
 * visible words open the name, which is what WCAG 2.5.3 Label in Name needs for speech input.
 *
 * Keep the visible words contiguous and first: "Export CSV <hidden>of …</hidden>", never
 * "Export <hidden>…</hidden> CSV", or the visible label no longer appears in the name as a phrase.
 * @param {HiddenContextTextProps} props - The translation function, key and interpolation values.
 * @returns {React.ReactElement} The label.
 */
/*
 * Trans interpolates `values` into the string and only then parses it for tags, and the app turns
 * escaping off (`escapeValue: false`). Unescaped, a value that holds "<" -- a crawl, snippet or
 * file name the user typed -- is parsed as markup: "Q1 <draft>.pdf" was named "Q1 &lt;draft>.pdf"
 * and "<1>" vanished. So values are escaped before the parse and unescaped after it, and reach
 * the name exactly as typed.
 */
const ESCAPE_VALUES = { interpolation: { escapeValue: true } };

const HiddenContextText: React.FC<HiddenContextTextProps> = ({ t, i18nKey, values }) => (
  <Trans
    t={t}
    i18nKey={i18nKey}
    values={values}
    components={HIDDEN_TEXT}
    tOptions={ESCAPE_VALUES}
    shouldUnescape
  />
);

export default HiddenContextText;
